import { Dialog, DialogConfig } from '@angular/cdk/dialog';
import { OverlayConfig } from '@angular/cdk/overlay';

import { AddDocumentComponent } from '@app/modules/vault/components/add-document/add-document.component';
import { EditMxPlayerComponent } from '@app/modules/vault/components/add-document/add-api-key-managed/edit-mx-player/edit-mx-player.component';

function isDialogComponentTriggerPlaidFlow(componentOrTemplateRef: unknown): boolean {
  return componentOrTemplateRef === AddDocumentComponent || componentOrTemplateRef === EditMxPlayerComponent;
}

const PATCHED = Symbol('kinnect.plaidOverlayLayering.patched');
const FORCE_NO_POPOVER = Symbol('kinnect.plaidOverlayLayering.forceNoPopover');

type DialogProtoLike = {
  open: (componentOrTemplateRef: unknown, config?: DialogConfig) => unknown;
  _getOverlayConfig: (config: DialogConfig) => OverlayConfig;
  [PATCHED]?: true;
};

/**
 * Ensures Plaid Link (third-party iframe overlay) can render above certain Angular dialogs.
 *
 * Angular CDK 21 overlays use the Popover API by default (top layer), which always paints above
 * normal DOM content. Plaid Link injects its iframe into the normal document, so it can appear
 * behind dialogs that are in the top layer. For the specific dialogs that can launch Plaid, we
 * force `usePopover: false` so they use normal z-index stacking instead.
 *
 * Affects:
 * - AddDocumentComponent
 * - EditMxPlayerComponent
 *
 * All other dialogs keep the CDK 21 default (usePopover: true).
 */
export function patchDialogForPlaidOverlayLayering(): void {
  const proto = Dialog.prototype as unknown as DialogProtoLike;

  if (proto[PATCHED]) return;
  if (typeof proto.open !== 'function' || typeof proto._getOverlayConfig !== 'function') return;

  const originalOpen = proto.open;
  const originalGetOverlayConfig = proto._getOverlayConfig;

  proto.open = function (this: Dialog, componentOrTemplateRef: unknown, config?: DialogConfig) {
    try {
      if (isDialogComponentTriggerPlaidFlow(componentOrTemplateRef)) {
        // CDK Dialog merges/clones config internally; mark with a Symbol so it survives object spread.
        const nextConfig = (config ?? {}) as DialogConfig & Record<symbol, unknown>;
        nextConfig[FORCE_NO_POPOVER] = true;
        return originalOpen.call(this, componentOrTemplateRef, nextConfig);
      }
      return originalOpen.call(this, componentOrTemplateRef, config);
    } catch {
      // Fail-safe: if CDK internals change, don't crash the app.
      return originalOpen.call(this, componentOrTemplateRef, config);
    }
  };

  proto._getOverlayConfig = function (this: Dialog, config: DialogConfig): OverlayConfig {
    try {
      const overlayConfig = originalGetOverlayConfig.call(this, config);
      if (
        config &&
        typeof config === 'object' &&
        (config as Record<symbol, unknown>)[FORCE_NO_POPOVER] === true &&
        overlayConfig &&
        typeof overlayConfig === 'object' &&
        'usePopover' in overlayConfig
      ) {
        overlayConfig.usePopover = false;
      }
      return overlayConfig;
    } catch {
      // Fail-safe: fall back to CDK defaults if anything unexpected occurs.
      return originalGetOverlayConfig.call(this, config);
    }
  };

  Object.defineProperty(proto, PATCHED, { value: true });
}

